// Smart Home Kits — standardised ways to turn a home smart, without a custom consultation.
// GenieX-engineered hardware. Installed & supported end-to-end by GenieX.
// Strict scope — do not invent extras beyond the items listed below.
import { BRAND } from "./content";

export const WA_PHONE = BRAND.phoneRaw.replace("+", ""); // 919063662999

export function waLink(message) {
  return `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(message)}`;
}

// Coverage identical across both Smart Value Pack / Luxury Smart Kit variants.
const ENTRANCE_ITEMS = "Video Doorbell (2-way audio · live video call on ring) · Smart Door Lock (Face · Fingerprint · Mobile app · RFID card · Mechanical key)";
const BATHROOM_ITEMS = "Motion sensor lights · Geyser automation (on/off + scheduling)";

const LUXURY_EXTRAS = [
  { room: "Living Room", items: "Curtain automation" },
  { room: "Whole Home", items: "Robot Vacuum Cleaner" },
  { room: "Kitchen", items: "Gas leak sensor" },
  { room: "Whole Home", items: "Fire / smoke safety sensor" },
  { room: "Balcony", items: "Door sensor" },
  { room: "Main Door", items: "Door sensor" },
  { room: "Whole Home", items: "5 × additional 16A Smart Plugs" },
];

export const SMART_HOME_KITS = [
  {
    key: "value-pack",
    name: "Smart Value Pack",
    eyebrow: "Start here",
    tagline: "The essential smart home",
    description:
      "Control the lights, fans, AC and TV in every room, let the bathroom lights take care of themselves, and greet visitors from anywhere — all from one app.",
    bestFor: "First-time smart home buyers who want the essentials done right.",
    whatItHelpsYouDo: "Control, convenience and a smarter front door — everywhere that matters.",
    image: "/products/value-pack-still.jpg",
    boxImage: "/products/value-pack-box.jpg",
    contentsImage: "/products/value-pack-2bhk-contents.jpg",
    highlight: false,
    canExpand: true,
    variants: [
      {
        key: "2bhk",
        label: "2 BHK",
        slug: "smart-value-pack-2bhk",
        price: 39999,
        mrp: 89999,
        priceLabel: "₹39,999",
        mrpLabel: "₹89,999",
        rooms: "2 Bedrooms · Living · Bathrooms · Entrance",
        coverage: [
          { room: "Living Room", items: "Lights · Fans · AC remote · TV remote" },
          { room: "2 Bedrooms", items: "Lights · Fans · AC remote · TV remote" },
          { room: "Bathrooms", items: BATHROOM_ITEMS },
          { room: "Entrance", items: ENTRANCE_ITEMS },
        ],
      },
      {
        key: "3bhk",
        label: "3 BHK",
        slug: "smart-value-pack-3bhk",
        price: 49999,
        mrp: 99999,
        priceLabel: "₹49,999",
        mrpLabel: "₹99,999",
        rooms: "3 Bedrooms · Living · Bathrooms · Entrance",
        coverage: [
          { room: "Living Room", items: "Lights · Fans · AC remote · TV remote" },
          { room: "3 Bedrooms", items: "Lights · Fans · AC remote · TV remote" },
          { room: "Bathrooms", items: BATHROOM_ITEMS },
          { room: "Entrance", items: ENTRANCE_ITEMS },
        ],
      },
    ],
  },
  {
    key: "luxury",
    name: "Luxury Smart Kit",
    eyebrow: "Most complete",
    tagline: "Smart living, comfort and safety",
    description:
      "Everything in the Smart Value Pack, plus curtains that open with the day, a robot vacuum that keeps floors spotless, and sensors that watch for gas, fire and open doors when you can't.",
    bestFor: "Homes that want the full GenieX experience — comfort, convenience and safety together.",
    whatItHelpsYouDo: "A richer, safer, more comfortable everyday home — with far less effort.",
    image: "/products/smart-curtain-kit.jpg",
    boxImage: "/products/value-pack-box.jpg",
    highlight: true,
    canExpand: true,
    variants: [
      {
        key: "2bhk",
        label: "2 BHK",
        slug: "luxury-smart-kit-2bhk",
        price: 89999,
        mrp: 149999,
        priceLabel: "₹89,999",
        mrpLabel: "₹1,49,999",
        rooms: "2 Bedrooms · Living · Bathrooms · Entrance · Curtains · Safety",
        coverage: [
          { room: "Living Room", items: "Lights · Fans · AC remote · TV remote" },
          { room: "2 Bedrooms", items: "Lights · Fans · AC remote · TV remote" },
          { room: "Bathrooms", items: BATHROOM_ITEMS },
          { room: "Entrance", items: ENTRANCE_ITEMS },
          ...LUXURY_EXTRAS,
        ],
      },
      {
        key: "3bhk",
        label: "3 BHK",
        slug: "luxury-smart-kit-3bhk",
        price: 99999,
        mrp: 164999,
        priceLabel: "₹99,999",
        mrpLabel: "₹1,64,999",
        rooms: "3 Bedrooms · Living · Bathrooms · Entrance · Curtains · Safety",
        coverage: [
          { room: "Living Room", items: "Lights · Fans · AC remote · TV remote" },
          { room: "3 Bedrooms", items: "Lights · Fans · AC remote · TV remote" },
          { room: "Bathrooms", items: BATHROOM_ITEMS },
          { room: "Entrance", items: ENTRANCE_ITEMS },
          ...LUXURY_EXTRAS,
        ],
      },
    ],
  },
  {
    key: "security",
    name: "Security Kit",
    eyebrow: "Protection first",
    tagline: "Peace of mind, at every entry point",
    description:
      "See who's at the door before you open it, know the moment a door or window opens when it shouldn't, and get an early warning on gas or fire — day or night.",
    bestFor: "Homes that want to lead with protection and awareness.",
    whatItHelpsYouDo: "Round-the-clock awareness of your home's entry points and early warning on gas/fire.",
    image: "/products/smart-doorlock-face-recognition.jpg",
    boxImage: "/products/smart-outdoor-camera.jpg",
    highlight: false,
    canExpand: true,
    variants: [
      {
        key: "standard",
        label: "All Homes",
        slug: "security-kit",
        price: 44999,
        mrp: 75999,
        priceLabel: "₹44,999",
        mrpLabel: "₹75,999",
        rooms: "Main Door · Balcony · Indoor · Outdoor",
        coverage: [
          { room: "Main Door", items: "Face Recognition Smart Door Lock" },
          { room: "Entrance", items: "Video Doorbell (2-way audio · live video call on ring)" },
          { room: "Kitchen", items: "Gas leak sensor" },
          { room: "Whole Home", items: "Smoke detecting sensor" },
          { room: "Outdoor", items: "1 Outdoor Camera" },
          { room: "Indoor", items: "1 Indoor Camera" },
          { room: "Main Door", items: "Door sensor" },
          { room: "Balcony", items: "Door sensor" },
        ],
      },
    ],
  },
];

// Backward-compatible flat list (2BHK/3BHK Smart Value Pack cards) — kept for any
// legacy references; new code should read SMART_HOME_KITS.
export const VALUE_PACKS = SMART_HOME_KITS[0].variants.map((v) => ({
  key: v.key,
  badge: `For ${v.label} Home`,
  title: "Smart Value Pack",
  sub: v.key === "2bhk" ? "Perfect for 2 BHK apartments" : "Most popular for 3 BHK homes",
  price: v.price,
  priceLabel: v.priceLabel,
  mrpLabel: v.mrpLabel,
  emi: `Starts ₹${Math.round(v.price / 24).toLocaleString("en-IN")}/mo · 24-mo no-cost EMI`,
  rooms: v.rooms,
  highlight: v.key === "3bhk",
  waMessage: `Hi GenieX, I'm interested in the ${v.label} Smart Value Pack at ${v.priceLabel}. Please share details and book a site visit.`,
  coverage: v.coverage,
}));

// Feature highlights — strictly the capabilities included in the packs.
export const PACK_FEATURES = [
  {
    iconKey: "lights",
    title: "Lights & Fans",
    body: "Every light and fan across living and bedrooms — controlled from your phone or with a voice command.",
  },
  {
    iconKey: "remote",
    title: "AC & TV Remotes",
    body: "Built-in IR control replaces stock remotes for ACs and TVs. One app, one voice — no juggling remotes.",
  },
  {
    iconKey: "motion",
    title: "Motion-sensor Bathrooms",
    body: "Lights switch on the moment you enter, off when you leave. Zero touch, zero wasted electricity.",
  },
  {
    iconKey: "geyser",
    title: "Geyser Automation",
    body: "Turn the geyser on from bed. Schedule it for your shower hours. Never leave it running by accident again.",
  },
  {
    iconKey: "doorbell",
    title: "Video Doorbell",
    body: "Two-way audio. The moment a visitor presses the bell, your phone rings as a live video call — answer from anywhere.",
  },
  {
    iconKey: "lock",
    title: "Smart Door Lock",
    body: "Unlock five ways — face, fingerprint, mobile app, RFID card, or the trusted mechanical key as fallback.",
  },
];

// What a customer can add later, one piece at a time — the expansion promise.
export const EXPANSION_ITEMS = [
  "Curtain automation",
  "Face recognition door lock",
  "Indoor & outdoor security cameras",
  "Gas, fire and door sensors",
  "Additional smart plugs",
  "Robot vacuum cleaner",
  "Water tank automation",
  "Main gate automation",
  "Smart scene control switches",
];

export const EMI_PARTNERS = [
  {
    key: "bajaj-finserv",
    name: "Bajaj Finserv",
    blurb: "0% interest EMI · Instant approval at home",
    logo: "/brand/bajaj-finserv-logo.jpeg",
    bg: "white",
  },
  {
    key: "razorpay",
    name: "Razorpay EMI",
    blurb: "Pay in 3, 6, 9, 12 months · All major cards",
    logo: null, // text only — Razorpay logo not available, render branded text
    bg: "dark",
  },
  {
    key: "pinelabs",
    name: "Pine Labs",
    blurb: "No-cost EMI at doorstep · Card-on-delivery",
    logo: "/brand/pinelabs-logo.png",
    bg: "white",
  },
];
